import React from 'react'
import cx from 'classnames'
import ReactHoverObserver from 'react-hover-observer'
import Navigation from '/js/views/shared/components/navigation'
import Wayfinder from '/js/views/shared/components/wayfinder'
import BgVideo from '/js/views/shared/components/contentParticles/bgVideo'
import Footer from '/js/views/shared/components/footer'
import DefaultClasses from '/js/util/defaultClasses'
import { generateThemeClasses } from '/js/util'
import WhiteLogo from '/img/svg/logo-white'
import DownIndicator from '/img/svg/down-indicator'
import Arrow from '/img/svg/arrow'
import InnovationEmblem from '/img/svg/emblem-innovation'
import PeopleEmblem from '/img/svg/emblem-people'
import EnvironmentEmblem from '/img/svg/emblem-environment'
import FoundationalElementsEmblem from '/img/svg/emblem-foundational-elements'

class Homepage extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      hoveredSection: null
    }
  }

  setHoveredSection = section => {
    this.setState({
      hoveredSection: section.replace(/\s+/g, '-').toLowerCase()
    })
  }

  unsetHoveredSection = () => {
    this.setState({ hoveredSection: null })
  }

  renderIcons() {
    const defaultClasses = 'absolute transition-fast'
    const horizontalClasses = 'top-half -mt-61'
    const verticalClasses = 'left-half -ml-61'

    return (
      <>
        <a href="/innovation" tabIndex="-1">
          <InnovationEmblem
            className={cx(
              `left-36 hover:scale-110 hover:fill-gator-orange hover:text-white ${defaultClasses} ${horizontalClasses}`,
              {
                'scale-110 fill-gator-orange text-white':
                  this.state.hoveredSection === 'innovation',
                'scale-100 text-gator-orange':
                  this.state.hoveredSection != 'innovation'
              }
            )}
          />
        </a>
        <a href="/people" tabIndex="-1">
          <PeopleEmblem
            className={cx(
              `top-36 hover:scale-110 hover:fill-asparagus-green hover:text-white ${defaultClasses} ${verticalClasses}`,
              {
                'scale-110 fill-asparagus-green text-white':
                  this.state.hoveredSection === 'people',
                'scale-100 text-asparagus-green':
                  this.state.hoveredSection != 'people'
              }
            )}
          />
        </a>
        <a href="/environment" tabIndex="-1">
          <EnvironmentEmblem
            className={cx(
              `right-36 hover:scale-110 hover:fill-tufts-blue hover:text-white ${defaultClasses} ${horizontalClasses}`,
              {
                'scale-110 fill-tufts-blue text-white':
                  this.state.hoveredSection === 'environment',
                'scale-100 text-tufts-blue':
                  this.state.hoveredSection != 'environment'
              }
            )}
          />
        </a>
        <a href="/foundational-elements" tabIndex="-1">
          <FoundationalElementsEmblem
            className={cx(
              `bottom-36 hover:scale-110 hover:fill-goldenrod-yellow hover:text-white ${defaultClasses} ${verticalClasses}`,
              {
                'scale-110 fill-goldenrod-yellow text-white':
                  this.state.hoveredSection === 'foundational-elements',
                'scale-100 text-goldenrod-yellow':
                  this.state.hoveredSection != 'foundational-elements'
              }
            )}
          />
        </a>
      </>
    )
  }

  renderSections() {
    const { columns } = this.props.pageData
    const flexOrders = [1, 3]

    return (
      <div className="container flex flex-col md:flex-row text-center flex-wrap items-center md:justify-between pt-30 lg:pt-50 md:max-h-1050">
        <div className="w-full xl:w-auto xl:order-2">
          <div className="relative w-344 h-344 mb-40 mx-auto">
            <div className="sunburst absolute inset-0"></div>
            {this.renderIcons()}
          </div>
        </div>
        {columns.map((column, colIndex) => (
          <div
            key={`column-${colIndex}`}
            className={`flex flex-col items-center xl:self-start xl:order-${flexOrders[colIndex]}`}
          >
            {column.sections.map((section, sectionIndex) => {
              const theme = generateThemeClasses(section.theme)
              return (
                <ReactHoverObserver
                  key={`section-${section.title}`}
                  onMouseEnter={() => this.setHoveredSection(section.title)}
                  onMouseLeave={this.unsetHoveredSection}
                >
                  <a
                    href={section.url}
                    data-track-gtm={`Homepage|${section.title}`}
                    className={cx(
                      'pb-40 mb-32 border-b border-columbia-gray max-w-320 flex flex-col items-center group',
                      {
                        'border-none':
                          colIndex === columns.length - 1 &&
                          sectionIndex === column.sections.length - 1,
                        'md:border-0':
                          sectionIndex === column.sections.length - 1
                      }
                    )}
                  >
                    <h2
                      className={`divider uppercase font-condensed text-24 pb-10 mb-15 w-full ${theme.textColor}`}
                    >
                      {section.title}
                    </h2>
                    <p
                      className="mb-20 w-full"
                      dangerouslySetInnerHTML={{ __html: section.text }}
                    />
                    <Arrow
                      className={`${theme.textColor} group-hover:scale-110 transition-fast`}
                    />
                  </a>
                </ReactHoverObserver>
              )
            })}
          </div>
        ))}
      </div>
    )
  }

  renderOverview() {
    const { overview } = this.props.pageData

    return (
      <div className="container pt-32 pb-35 px-25 border-smoke-white border-b-4">
        <div className="text-center max-w-736 mx-auto">
          <h2 className="text-danaher-blue uppercase font-condensed text-24 mb-5 lg:mb-15">
            {overview.title}
          </h2>
          <p
            className="lg:text-18 mb-32 lg:mb-40"
            dangerouslySetInnerHTML={{ __html: overview.text }}
          />
          <div className="flex flex-col sm:flex-row items-center justify-center">
            {overview.links.map((link, index) => (
              <a
                key={`overview-button-${link.text}`}
                href={link.url}
                data-track-gtm={`Homepage|${link.text}`}
                className={`w-full sm:w-auto ${DefaultClasses.button} ${
                  DefaultClasses.buttonBlue
                } ${index < (overview.links.length - 1) ? 'mb-15 sm:mb-0 sm:mr-15' : ''}`}
              >
                {link.text}
              </a>
            ))}
          </div>
        </div>
      </div>
    )
  }

  renderHero() {
    const { routes, currentPage, pageData, shared, theme } = this.props
    const reportUrl = routes[1].url
    const pdfUrl = shared.navigation.pdfs[0].url

    return (
      <>
        <div
          data-label="homepage-hero"
          className="homepage-hero bg-columbia-gray relative overflow-hidden max-h-800 lg:min-h-720"
        >
          <Wayfinder routes={routes} currentPage={currentPage} theme={theme} />
          <div className="p-25 w-full h-full relative z-10">
            <BgVideo bgVideo={pageData.bgVideo} />
            <div className="absolute h-full w-full border border-white rounded-tr rounded-bl relative inset-0">
              <div className="pt-45 sm:pt-115 px-15 container flex flex-col items-center justify-center text-white text-center max-w-736 w-full h-full">
                <WhiteLogo className="mb-30 sm:mb-115 w-178 max-h-50" />
                <h1 className="font-serif text-30 sm:text-48 leading-tight divider pb-25 sm:pb-20 mb-10 sm:mb-15 w-full">
                  <span className="screenreader">Danaher </span>
                  {shared.header.year} {shared.header.report_name}
                </h1>
                <p className="mb-15 sm:mb-25 w-full" dangerouslySetInnerHTML={{ __html: pageData.intro }}/>
                <div data-label="hero-ctas" className="flex flex-row md:mb-60">
                  <a
                    href={reportUrl}
                    data-track-gtm="Homepage|Enter Site"
                    className={`mx-5 ${DefaultClasses.button} ${DefaultClasses.buttonWhite}`}
                  >
                    Enter Site
                  </a>
                  <a
                    href={pdfUrl}
                    data-track-gtm="Homepage|Download The PDF"
                    className={`mx-5 ${DefaultClasses.button} ${DefaultClasses.buttonWhite}`}
                  >
                    Download The PDF
                  </a>
                </div>
                <DownIndicator className="hidden md:block reverse-translate-y animate-infinite" />
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  render() {
    const { routes, currentPage, shared, theme } = this.props

    return (
      <>
        <Navigation
          navigation={shared.navigation}
          social={shared.social}
          currentPage={currentPage}
          routes={routes}
          theme={theme}
        />
        <div data-label="homepage" className="pb-55">
          {this.renderHero()} {this.renderOverview()} {this.renderSections()}
        </div>
        <Footer theme={theme} social={shared.social} footer={shared.footer} />
      </>
    )
  }
}

export default Homepage
